import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ModuleProps } from '../types';
import AppHeader from '../AppHeader';
import { useViewportHeight } from '../hooks/useViewportHeight';
import { useLanguage } from '../LanguageContext';
import { el, en } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface WritebackProposal {
  target: 'issue' | 'risk' | 'task' | 'none';
  set_status: string;
  append_note: string;
}

interface AgendaItem {
  id: string;
  kind: string;
  source_type: 'issue' | 'risk' | 'task' | null;
  source_id: string | null;
  category: string;
  title: string;
  notes: string;
  note_type: string;
  order_index: number;
  ai_generated: boolean;
  ai_rationale: string;
  post_meeting_status?: string | null;
  post_meeting_summary?: string | null;
  post_meeting_proposal?: WritebackProposal | null;
  post_meeting_applied?: boolean;
}

interface NewAction { title: string; assignee: string; note: string; }

interface Meeting {
  id: string;
  title: string;
  meeting_type: string;
  meeting_date: string | null;
  participant_stakeholder_ids: string[];
  status: string;
  created_by_name: string;
  created_at: string;
  agenda_items?: AgendaItem[];
  transcript_id?: string | null;
  agenda_snapshot?: any;
  matched_at?: string | null;
  match_new_actions?: NewAction[];
  match_general_notes?: string;
  last_generation?: { open_items: number; proposed: number; inserted: number; reason: string };
}

interface OpenItem {
  source_type: 'issue' | 'risk' | 'task';
  source_id: string;
  title: string;
  owner: string;
  status: string;
}

interface TranscriptLite { id: string; title: string; meeting_date?: string | null; ai_processed: boolean; created_at: string; }

const OUTCOME_BADGE: Record<string, { label: string; color: string }> = {
  discussed_resolved: { label: 'Λύθηκε', color: '#16a34a' },
  decision_made: { label: 'Απόφαση', color: '#4f46e5' },
  discussed_open: { label: 'Παραμένει ανοιχτό', color: '#f59e0b' },
  not_discussed: { label: 'Δεν συζητήθηκε', color: '#9ca3af' },
};

const CATEGORIES = ['decision_needed', 'blocked_on_them', 'update_for_them', 'risk_to_flag', 'other'];

const CATEGORY_COLOR: Record<string, string> = {
  decision_needed: '#ef4444',
  blocked_on_them: '#f59e0b',
  update_for_them: '#0ea5e9',
  risk_to_flag: '#ec4899',
  other: '#6b7280',
};

const SOURCE_BADGE: Record<string, { icon: string; label: string; color: string }> = {
  issue: { icon: '🔧', label: 'Issue', color: '#f59e0b' },
  risk: { icon: '⚠️', label: 'Risk', color: '#ef4444' },
  task: { icon: '✅', label: 'Task', color: '#8b5cf6' },
};

const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Meetings: React.FC<ModuleProps> = ({ project, user, token, onBack, canEdit, onLogout, onProfileEdit }) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = (M as any).meetings;
  const C = M.common;
  const headers = { Authorization: `Bearer ${token}` };

  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Meeting | null>(null);
  const [stakeholders, setStakeholders] = useState<any[]>([]);
  const [openItems, setOpenItems] = useState<OpenItem[]>([]);
  const [transcripts, setTranscripts] = useState<TranscriptLite[]>([]);
  const [linkPick, setLinkPick] = useState('');
  const [linking, setLinking] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [matching, setMatching] = useState(false);
  const [showComplete, setShowComplete] = useState(false);

  const [viewMonth, setViewMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  const viewportHeight = useViewportHeight();

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState<{ title: string; meeting_type: string; meeting_date: string; participant_stakeholder_ids: string[] }>({
    title: '', meeting_type: '', meeting_date: '', participant_stakeholder_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [genMsg, setGenMsg] = useState<{ tone: 'ok' | 'info' | 'error'; text: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Meeting | null>(null);

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editBuf, setEditBuf] = useState<{ title: string; category: string; notes: string; note_type: string }>({ title: '', category: 'other', notes: '', note_type: 'talking_point' });
  const [showAddItem, setShowAddItem] = useState(false);
  const [showOther, setShowOther] = useState(false);
  const [addMode, setAddMode] = useState<'adhoc' | 'linked'>('adhoc');
  const [addBuf, setAddBuf] = useState<{ title: string; category: string; source: string }>({ title: '', category: 'other', source: '' });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [mRes, sRes, oRes, tRes] = await Promise.allSettled([
        axios.get(`${API}/meetings/${project.id}`, { headers }),
        axios.get(`${API}/modules/${project.id}/Stakeholders`, { headers }),
        axios.get(`${API}/meetings/${project.id}/open-items`, { headers }),
        axios.get(`${API}/meetings/${project.id}/transcripts`, { headers }),
      ]);
      if (mRes.status === 'fulfilled') setMeetings(mRes.value.data || []);
      if (sRes.status === 'fulfilled') setStakeholders(sRes.value.data?.data?.stakeholders || []);
      if (oRes.status === 'fulfilled') setOpenItems(oRes.value.data || []);
      if (tRes.status === 'fulfilled') setTranscripts(tRes.value.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openMeeting = async (id: string) => {
    setGenMsg(null);
    try {
      const res = await axios.get(`${API}/meetings/${project.id}/${id}`, { headers });
      setSelected(res.data);
    } catch (err) { console.error(err); }
  };

  const refreshSelected = async () => { if (selected) openMeeting(selected.id); };

  const handleCreate = async () => {
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      const res = await axios.post(`${API}/meetings/${project.id}`, form, { headers });
      setMeetings(prev => [res.data, ...prev]);
      setShowCreate(false);
      setForm({ title: '', meeting_type: '', meeting_date: '', participant_stakeholder_ids: [] });
      openMeeting(res.data.id);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleGenerate = async () => {
    if (!selected) return;
    setGenerating(true);
    setGenMsg(null);
    try {
      const res = await axios.post(`${API}/meetings/${project.id}/${selected.id}/generate-agenda`, {}, { headers });
      setSelected(res.data);
      setMeetings(prev => prev.map(m => m.id === selected.id ? { ...m, status: res.data.status } : m));
      const g = res.data.last_generation;
      const capNote = g?.capped > 0 ? ' ' + L.genCapped.replace('{n}', String(g.capped)) : '';
      if (g?.reason === 'no_open_items') setGenMsg({ tone: 'info', text: L.genNoOpenItems });
      else if (g?.reason === 'no_participants') setGenMsg({ tone: 'info', text: L.genNoParticipants.replace('{n}', String(g.inserted)) + capNote });
      else if (g?.reason === 'no_match') setGenMsg({ tone: 'info', text: L.genNoMatch.replace('{n}', String(g.open_items)) });
      else if (g) setGenMsg({ tone: 'ok', text: L.genDone.replace('{n}', String(g.inserted)) + capNote });
    } catch (err: any) {
      setGenMsg({ tone: 'error', text: err?.response?.data?.message || L.genError });
    }
    finally { setGenerating(false); }
  };

  // ── Increment 3: transcript link + post-meeting ──
  const applySelected = (data: Meeting) => {
    setSelected(data);
    setMeetings(prev => prev.map(m => m.id === data.id ? { ...m, status: data.status, transcript_id: data.transcript_id } : m));
  };

  const linkTranscript = async (transcriptId: string | null) => {
    if (!selected) return;
    setLinking(true);
    setGenMsg(null);
    try {
      const res = await axios.put(`${API}/meetings/${project.id}/${selected.id}/transcript`, { transcript_id: transcriptId }, { headers });
      applySelected(res.data);
      setLinkPick('');
    } catch (err: any) {
      setGenMsg({ tone: 'error', text: err?.response?.data?.message || L.genError });
    } finally { setLinking(false); }
  };

  const handleComplete = async () => {
    if (!selected) return;
    setCompleting(true);
    setGenMsg(null);
    try {
      const res = await axios.post(`${API}/meetings/${project.id}/${selected.id}/complete`, {}, { headers });
      applySelected(res.data);
      setShowComplete(false);
      setGenMsg({ tone: 'ok', text: L.completedMsg });
    } catch (err: any) {
      setGenMsg({ tone: 'error', text: err?.response?.data?.message || L.genError });
    } finally { setCompleting(false); }
  };

  const handleMatch = async () => {
    if (!selected) return;
    setMatching(true);
    setGenMsg(null);
    try {
      const res = await axios.post(`${API}/meetings/${project.id}/${selected.id}/match-transcript`, {}, { headers });
      applySelected(res.data);
      setGenMsg({ tone: 'ok', text: L.matchDone });
    } catch (err: any) {
      setGenMsg({ tone: 'error', text: err?.response?.data?.message || L.matchError });
    } finally { setMatching(false); }
  };

  const applyWriteback = async (itemId: string) => {
    if (!selected) return;
    try {
      const res = await axios.post(`${API}/meetings/${project.id}/${selected.id}/agenda-items/${itemId}/apply-writeback`, {}, { headers });
      applySelected(res.data);
      fetchAll();
    } catch (err: any) {
      setGenMsg({ tone: 'error', text: err?.response?.data?.message || L.genError });
    }
  };

  const dismissWriteback = async (itemId: string) => {
    if (!selected) return;
    try {
      const res = await axios.post(`${API}/meetings/${project.id}/${selected.id}/agenda-items/${itemId}/dismiss-writeback`, {}, { headers });
      applySelected(res.data);
    } catch (err) { console.error(err); }
  };

  const createActionTask = async (action: NewAction) => {
    if (!selected) return;
    try {
      const res = await axios.post(`${API}/meetings/${project.id}/${selected.id}/new-actions`, action, { headers });
      setSelected(res.data.meeting);
      fetchAll();
    } catch (err: any) {
      setGenMsg({ tone: 'error', text: err?.response?.data?.message || L.genError });
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/meetings/${project.id}/${deleteTarget.id}`, { headers });
      setMeetings(prev => prev.filter(m => m.id !== deleteTarget.id));
      if (selected?.id === deleteTarget.id) setSelected(null);
    } catch (err) { console.error(err); }
    finally { setDeleteTarget(null); }
  };

  // ── agenda item editing ──
  const startEdit = (a: AgendaItem) => {
    setEditingId(a.id);
    setEditBuf({ title: a.title, category: a.category, notes: a.notes || '', note_type: a.note_type || 'talking_point' });
  };
  const saveEdit = async (id: string) => {
    if (!selected) return;
    try {
      await axios.put(`${API}/meetings/${project.id}/${selected.id}/agenda-items/${id}`, editBuf, { headers });
      setEditingId(null);
      refreshSelected();
    } catch (err) { console.error(err); }
  };
  const removeItem = async (id: string) => {
    if (!selected) return;
    try {
      await axios.delete(`${API}/meetings/${project.id}/${selected.id}/agenda-items/${id}`, { headers });
      refreshSelected();
    } catch (err) { console.error(err); }
  };
  const move = async (idx: number, dir: -1 | 1) => {
    if (!selected?.agenda_items) return;
    const items = [...selected.agenda_items].sort((a, b) => a.order_index - b.order_index);
    const j = idx + dir;
    if (j < 0 || j >= items.length) return;
    [items[idx], items[j]] = [items[j], items[idx]];
    try {
      await axios.put(`${API}/meetings/${project.id}/${selected.id}/agenda-items/reorder`, { ordered_ids: items.map(i => i.id) }, { headers });
      refreshSelected();
    } catch (err) { console.error(err); }
  };
  const addItem = async () => {
    if (!selected) return;
    const body: any = { category: addBuf.category };
    if (addMode === 'adhoc') {
      if (!addBuf.title.trim()) return;
      body.title = addBuf.title;
    } else {
      if (!addBuf.source) return;
      const [st, sid] = addBuf.source.split('::');
      const oi = openItems.find(o => o.source_type === st && o.source_id === sid);
      body.source_type = st; body.source_id = sid; body.title = oi?.title || '';
    }
    try {
      await axios.post(`${API}/meetings/${project.id}/${selected.id}/agenda-items`, body, { headers });
      setShowAddItem(false);
      setAddBuf({ title: '', category: 'other', source: '' });
      refreshSelected();
    } catch (err) { console.error(err); }
  };

  const quickAddLinked = async (o: OpenItem) => {
    if (!selected) return;
    try {
      await axios.post(`${API}/meetings/${project.id}/${selected.id}/agenda-items`,
        { source_type: o.source_type, source_id: o.source_id, title: o.title, category: o.source_type === 'risk' ? 'risk_to_flag' : 'other' },
        { headers });
      refreshSelected();
    } catch (err) { console.error(err); }
  };

  const toggleParticipant = (id: string) => {
    setForm(f => ({
      ...f,
      participant_stakeholder_ids: f.participant_stakeholder_ids.includes(id)
        ? f.participant_stakeholder_ids.filter(x => x !== id)
        : [...f.participant_stakeholder_ids, id],
    }));
  };
  const participantNames = (ids: string[]) =>
    stakeholders.filter(s => ids.includes(s.id)).map(s => s.name).join(', ') || '—';
  const fmtDate = (d: string | null) => d ? new Date(d).toLocaleDateString(language === 'en' ? 'en-GB' : 'el-GR') : '—';

  const card: React.CSSProperties = { background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb', padding: 16 };
  const catLabel = (c: string) => L.category[c] || c;

  // ── calendar helpers ──
  const monthLabel = viewMonth.toLocaleDateString(language === 'en' ? 'en-GB' : 'el-GR', { month: 'long', year: 'numeric' });
  const buildGrid = () => {
    const first = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
    const startDow = (first.getDay() + 6) % 7; // Monday=0
    const start = new Date(first); start.setDate(first.getDate() - startDow);
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  };
  const meetingsByDay: Record<string, Meeting[]> = {};
  for (const m of meetings) if (m.meeting_date) {
    const k = iso(new Date(m.meeting_date));
    (meetingsByDay[k] ||= []).push(m);
  }
  const noDateMeetings = meetings.filter(m => !m.meeting_date);
  const dow = language === 'en' ? ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] : ['Δε', 'Τρ', 'Τε', 'Πε', 'Πα', 'Σα', 'Κυ'];

  return (
    <div style={{ height: viewportHeight, background: '#f0f2f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />
      <div style={{ padding: '14px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: 14, flexShrink: 0 }}>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 500 }} onClick={onBack}>{C.myProjects}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#4f46e5', cursor: 'pointer', fontWeight: 500 }} onClick={onBack}>{project.name}</span>
        <span style={{ color: '#9ca3af', margin: '0 8px' }}>/</span>
        <span style={{ color: '#374151', fontWeight: 500 }}>{L.title}</span>
      </div>
      <div style={{
        flex: 1, minHeight: 0, width: '100%', boxSizing: 'border-box',
        padding: '18px 32px', maxWidth: selected ? 1100 : 1300, margin: '0 auto',
        display: 'flex', flexDirection: 'column',
        overflowY: selected ? 'auto' : 'hidden',
      }}>
        {!selected ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#111', textTransform: 'capitalize', minWidth: 220 }}>{monthLabel}</div>
                <button onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', fontSize: 15, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>‹</button>
                <button onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '50%', width: 34, height: 34, cursor: 'pointer', fontSize: 15, color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>›</button>
                <button onClick={() => { const d = new Date(); setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1)); }}
                  style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 999, padding: '6px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#6b7280' }}>{L.today}</button>
              </div>
              {canEdit && (
                <button onClick={() => { setForm({ title: '', meeting_type: '', meeting_date: '', participant_stakeholder_ids: [] }); setShowCreate(true); }}
                  style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(79,70,229,0.3)' }}>
                  {L.newMeeting}
                </button>
              )}
            </div>

            {loading ? (
              <div style={{ ...card, textAlign: 'center', color: '#9ca3af' }}>{C.loading}</div>
            ) : (
              <>
                <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', background: '#fff', borderRadius: 16, border: '1px solid #ebe9e4', boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.04)', overflow: 'hidden' }}>
                  <div style={{ flexShrink: 0, display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', background: '#faf9f7', borderBottom: '1px solid #f0efec' }}>
                    {dow.map((d, di) => (
                      <div key={d} style={{ padding: '10px 8px', fontSize: 11, fontWeight: 700, color: di >= 5 ? '#c4b8a8' : '#a8a29a', textAlign: 'center', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{d}</div>
                    ))}
                  </div>
                  <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: 'repeat(7,1fr)', gridTemplateRows: 'repeat(6,1fr)' }}>
                    {buildGrid().map((d, i) => {
                      const inMonth = d.getMonth() === viewMonth.getMonth();
                      const key = iso(d);
                      const dayMeetings = meetingsByDay[key] || [];
                      const isToday = key === iso(new Date());
                      const isWeekend = i % 7 >= 5;
                      return (
                        <div key={i}
                          onClick={() => { if (canEdit) { setForm({ title: '', meeting_type: '', meeting_date: key, participant_stakeholder_ids: [] }); setShowCreate(true); } }}
                          onMouseEnter={e => { if (canEdit && inMonth) (e.currentTarget as HTMLDivElement).style.background = '#f5f3ff'; }}
                          onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = inMonth ? (isWeekend ? '#fdfcfb' : '#fff') : '#faf9f7'; }}
                          style={{ minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', borderRight: (i % 7 !== 6) ? '1px solid #f3f2ef' : 'none', borderBottom: i < 35 ? '1px solid #f3f2ef' : 'none', padding: '5px 6px', background: inMonth ? (isWeekend ? '#fdfcfb' : '#fff') : '#faf9f7', cursor: canEdit ? 'pointer' : 'default', transition: 'background 0.12s' }}>
                          <div style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
                            <span style={{
                              fontSize: 12, fontWeight: isToday ? 700 : 500,
                              color: !inMonth ? '#d6d1c8' : isToday ? '#fff' : '#57534e',
                              width: 22, height: 22, borderRadius: '50%',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              background: isToday ? '#4f46e5' : 'transparent',
                            }}>{d.getDate()}</span>
                          </div>
                          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                            {dayMeetings.slice(0, 4).map(m => (
                              <div key={m.id} onClick={e => { e.stopPropagation(); openMeeting(m.id); }}
                                title={m.title}
                                onMouseEnter={e => ((e.currentTarget as HTMLDivElement).style.background = '#ddd6fe')}
                                onMouseLeave={e => ((e.currentTarget as HTMLDivElement).style.background = '#ede9fe')}
                                style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 5, fontSize: 10.5, background: '#ede9fe', color: '#4338ca', borderRadius: 5, padding: '3px 6px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600, transition: 'background 0.12s' }}>
                                <span style={{ width: 5, height: 5, borderRadius: '50%', background: m.status === 'agenda_ready' ? '#22c55e' : '#a78bfa', flexShrink: 0 }} />
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.title}</span>
                              </div>
                            ))}
                            {dayMeetings.length > 4 && <div style={{ fontSize: 10, color: '#a8a29a', padding: '0 6px', fontWeight: 600, flexShrink: 0 }}>+{dayMeetings.length - 4}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {noDateMeetings.length > 0 && (
                  <div style={{ marginTop: 12, flexShrink: 0 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{L.noDate}</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 132, overflowY: 'auto' }}>
                      {noDateMeetings.map(m => (
                        <div key={m.id} onClick={() => openMeeting(m.id)} style={{ ...card, cursor: 'pointer', padding: '9px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <span style={{ fontSize: 14, fontWeight: 600, color: '#111' }}>{m.title}</span>
                          <span style={{ fontSize: 11, color: '#9ca3af' }}>{m.meeting_type || ''} · {participantNames(m.participant_stakeholder_ids)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <>
            <button onClick={() => { setSelected(null); setEditingId(null); setShowAddItem(false); setGenMsg(null); setShowComplete(false); setLinkPick(''); setShowOther(false); }}
              style={{ background: 'none', border: 'none', color: '#4f46e5', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
              ← {L.backToList}
            </button>

            <div style={{ ...card, marginBottom: 14 }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontSize: 19, fontWeight: 600, color: '#111' }}>{selected.title}</div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                    {selected.meeting_type ? `${selected.meeting_type} · ` : ''}{fmtDate(selected.meeting_date)}
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
                    {L.participants}: {participantNames(selected.participant_stakeholder_ids)}
                  </div>
                </div>
                {canEdit && (
                  <button onClick={() => setDeleteTarget(selected)}
                    style={{ background: 'none', border: '1px solid #fee2e2', color: '#ef4444', borderRadius: 6, padding: '5px 10px', fontSize: 12, cursor: 'pointer' }}>
                    {C.delete}
                  </button>
                )}
              </div>
            </div>

            {(() => {
              const linked = selected.transcript_id ? transcripts.find(t => t.id === selected.transcript_id) : null;
              const held = selected.status === 'held';
              const stLabel = L.status[selected.status] || selected.status;
              const stColor = held ? '#16a34a' : selected.status === 'agenda_ready' ? '#4f46e5' : '#9ca3af';
              return (
                <div style={{ ...card, marginBottom: 14, padding: '14px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 999, background: stColor + '18', color: stColor }}>{stLabel}</span>
                    {selected.matched_at && <span style={{ fontSize: 11, color: '#9ca3af' }}>{L.lastMatch}: {fmtDate(selected.matched_at)}</span>}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>{L.transcriptLabel}:</span>
                    {linked ? (
                      <>
                        <span style={{ fontSize: 13, color: '#111', background: '#ede9fe', borderRadius: 6, padding: '4px 10px' }}>📄 {linked.title}</span>
                        {canEdit && <button onClick={() => linkTranscript(null)} disabled={linking}
                          style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 12, cursor: 'pointer' }}>{L.unlink}</button>}
                      </>
                    ) : selected.transcript_id ? (
                      <span style={{ fontSize: 13, color: '#111', background: '#ede9fe', borderRadius: 6, padding: '4px 10px' }}>📄 {L.transcriptLinked}</span>
                    ) : canEdit ? (
                      transcripts.length ? (
                        <>
                          <select value={linkPick} onChange={e => setLinkPick(e.target.value)}
                            style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, minWidth: 220 }}>
                            <option value="">{L.pickTranscript}</option>
                            {transcripts.map(t => <option key={t.id} value={t.id}>{t.title}{t.ai_processed ? '' : ` (${L.processing})`}</option>)}
                          </select>
                          <button onClick={() => linkPick && linkTranscript(linkPick)} disabled={!linkPick || linking}
                            style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '7px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!linkPick || linking) ? 0.5 : 1 }}>{L.link}</button>
                        </>
                      ) : <span style={{ fontSize: 12, color: '#9ca3af' }}>{L.noTranscripts}</span>
                    ) : <span style={{ fontSize: 12, color: '#9ca3af' }}>—</span>}
                  </div>

                  {canEdit && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
                      {!held && !!selected.agenda_items?.length && (
                        <button onClick={() => setShowComplete(true)}
                          style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                          {L.completeBtn}
                        </button>
                      )}
                      {held && (
                        <button onClick={handleMatch} disabled={matching || !selected.transcript_id}
                          title={!selected.transcript_id ? L.matchNeedsTranscript : ''}
                          style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (matching || !selected.transcript_id) ? 0.5 : 1 }}>
                          {matching ? L.matching : (selected.matched_at ? L.rematchBtn : L.matchBtn)}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#111' }}>{L.agenda}</div>
              {canEdit && selected.status !== 'held' && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowAddItem(v => !v)}
                    style={{ background: '#fff', color: '#4f46e5', border: '1px solid #c7d2fe', borderRadius: 8, padding: '8px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                    {L.addTopic}
                  </button>
                  <button onClick={handleGenerate} disabled={generating}
                    style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: generating ? 0.6 : 1 }}>
                    {generating ? L.generating : (selected.agenda_items?.length ? L.regenerate : L.generate)}
                  </button>
                </div>
              )}
            </div>

            {(() => {
              const scope = Array.from(new Set(
                stakeholders
                  .filter(s => selected.participant_stakeholder_ids.includes(s.id))
                  .flatMap(s => [s.team, s.organization])
                  .filter(Boolean),
              ));
              return scope.length > 0 ? (
                <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 10 }}>
                  {L.participantScope}: {scope.map((x, i) => (
                    <span key={i} style={{ background: '#eef2ff', color: '#4338ca', borderRadius: 6, padding: '2px 7px', marginRight: 5, fontWeight: 600 }}>{x}</span>
                  ))}
                </div>
              ) : null;
            })()}

            {genMsg && (
              <div style={{
                marginBottom: 10, padding: '10px 14px', borderRadius: 10, fontSize: 13, lineHeight: 1.45,
                background: genMsg.tone === 'error' ? '#fef2f2' : genMsg.tone === 'ok' ? '#f0fdf4' : '#f5f3ff',
                border: `1px solid ${genMsg.tone === 'error' ? '#fecaca' : genMsg.tone === 'ok' ? '#bbf7d0' : '#ddd6fe'}`,
                color: genMsg.tone === 'error' ? '#b91c1c' : genMsg.tone === 'ok' ? '#166534' : '#5b21b6',
                display: 'flex', alignItems: 'flex-start', gap: 8,
              }}>
                <span>{genMsg.tone === 'error' ? '⚠️' : genMsg.tone === 'ok' ? '✓' : 'ℹ️'}</span>
                <span style={{ flex: 1 }}>{genMsg.text}</span>
                <button onClick={() => setGenMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.6, fontSize: 14, padding: 0 }}>✕</button>
              </div>
            )}

            {showAddItem && (
              <div style={{ ...card, marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  {(['adhoc', 'linked'] as const).map(mode => (
                    <button key={mode} onClick={() => setAddMode(mode)}
                      style={{ flex: 1, padding: '7px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: addMode === mode ? '#4f46e5' : '#f3f4f6', color: addMode === mode ? '#fff' : '#6b7280' }}>
                      {mode === 'adhoc' ? L.addAdhoc : L.addLinked}
                    </button>
                  ))}
                </div>
                {addMode === 'adhoc' ? (
                  <input value={addBuf.title} onChange={e => setAddBuf({ ...addBuf, title: e.target.value })} placeholder={L.topicPh}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }} />
                ) : (
                  <select value={addBuf.source} onChange={e => setAddBuf({ ...addBuf, source: e.target.value })}
                    style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', marginBottom: 8 }}>
                    <option value="">{L.pickItem}</option>
                    {openItems.map(o => (
                      <option key={`${o.source_type}::${o.source_id}`} value={`${o.source_type}::${o.source_id}`}>
                        [{SOURCE_BADGE[o.source_type].label}] {o.title}{o.owner ? ` — ${o.owner}` : ''}
                      </option>
                    ))}
                  </select>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select value={addBuf.category} onChange={e => setAddBuf({ ...addBuf, category: e.target.value })}
                    style={{ padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, flex: 1 }}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
                  </select>
                  <button onClick={addItem} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{C.save}</button>
                </div>
              </div>
            )}

            {!selected.agenda_items?.length ? (
              <div style={{ ...card, textAlign: 'center', color: '#9ca3af', padding: 32 }}>{L.noAgenda}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[...selected.agenda_items].sort((a, b) => a.order_index - b.order_index).map((a, idx, arr) => {
                  const b = a.source_type ? SOURCE_BADGE[a.source_type] : null;
                  const editing = editingId === a.id;
                  return (
                    <div key={a.id} style={{ ...card, padding: '12px 14px' }}>
                      {editing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                          <input value={editBuf.title} onChange={e => setEditBuf({ ...editBuf, title: e.target.value })}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box' }} />
                          <div style={{ display: 'flex', gap: 8 }}>
                            <select value={editBuf.category} onChange={e => setEditBuf({ ...editBuf, category: e.target.value })}
                              style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12, flex: 1 }}>
                              {CATEGORIES.map(c => <option key={c} value={c}>{catLabel(c)}</option>)}
                            </select>
                            <select value={editBuf.note_type} onChange={e => setEditBuf({ ...editBuf, note_type: e.target.value })}
                              style={{ padding: '6px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 12 }}>
                              <option value="talking_point">{L.noteTalkingPoint}</option>
                              <option value="decision">{L.noteDecision}</option>
                            </select>
                          </div>
                          <textarea value={editBuf.notes} onChange={e => setEditBuf({ ...editBuf, notes: e.target.value })} placeholder={L.notesPh} rows={2}
                            style={{ width: '100%', padding: '7px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 13, boxSizing: 'border-box', resize: 'vertical', fontFamily: 'inherit' }} />
                          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                            <button onClick={() => setEditingId(null)} style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, cursor: 'pointer' }}>{C.cancel}</button>
                            <button onClick={() => saveEdit(a.id)} style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{C.save}</button>
                          </div>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                              <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, background: CATEGORY_COLOR[a.category] + '18', color: CATEGORY_COLOR[a.category], fontWeight: 700 }}>{catLabel(a.category)}</span>
                              {b && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, background: b.color + '18', color: b.color, fontWeight: 700 }}>{b.icon} {b.label}</span>}
                              {a.note_type === 'decision' && <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, background: '#dcfce7', color: '#166534', fontWeight: 700 }}>★ {L.noteDecision}</span>}
                            </div>
                            <div style={{ fontSize: 14, color: '#111', fontWeight: 500, marginTop: 5 }}>{a.title}</div>
                            {a.notes && (
                              a.note_type === 'decision' ? (
                                <div style={{ marginTop: 6, background: '#f0fdf4', border: '1px solid #bbf7d0', borderLeft: '3px solid #16a34a', borderRadius: 6, padding: '6px 10px' }}>
                                  <div style={{ fontSize: 10, fontWeight: 700, color: '#166534', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>★ {L.noteDecision}</div>
                                  <div style={{ fontSize: 12.5, color: '#14532d', whiteSpace: 'pre-wrap' }}>{a.notes}</div>
                                </div>
                              ) : (
                                <div style={{ fontSize: 12, color: '#374151', marginTop: 4, whiteSpace: 'pre-wrap' }}>📝 {a.notes}</div>
                              )
                            )}
                            {a.ai_rationale && <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 4, fontStyle: 'italic' }}>✦ {a.ai_rationale}</div>}

                            {a.post_meeting_status && (
                              <div style={{ marginTop: 8, borderTop: '1px dashed #e5e7eb', paddingTop: 8 }}>
                                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 999, fontWeight: 700, background: (OUTCOME_BADGE[a.post_meeting_status]?.color || '#6b7280') + '18', color: OUTCOME_BADGE[a.post_meeting_status]?.color || '#6b7280' }}>
                                  {L.outcome[a.post_meeting_status] || a.post_meeting_status}
                                </span>
                                {a.post_meeting_summary && <div style={{ fontSize: 12, color: '#374151', marginTop: 5, whiteSpace: 'pre-wrap' }}>💬 {a.post_meeting_summary}</div>}

                                {a.post_meeting_proposal && a.post_meeting_proposal.target !== 'none' && !a.post_meeting_applied && (
                                  <div style={{ marginTop: 8, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 8, padding: '9px 12px' }}>
                                    <div style={{ fontSize: 11, fontWeight: 700, color: '#5b21b6', marginBottom: 4 }}>{L.proposalTitle}</div>
                                    {a.post_meeting_proposal.set_status && <div style={{ fontSize: 12, color: '#374151' }}>• {L.proposalStatus}: <strong>{a.post_meeting_proposal.set_status}</strong></div>}
                                    {a.post_meeting_proposal.append_note && <div style={{ fontSize: 12, color: '#374151' }}>• {L.proposalNote}: {a.post_meeting_proposal.append_note}</div>}
                                    {canEdit && (
                                      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                                        <button onClick={() => applyWriteback(a.id)}
                                          style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{L.applyBtn}</button>
                                        <button onClick={() => dismissWriteback(a.id)}
                                          style={{ background: '#f3f4f6', color: '#6b7280', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{L.dismissBtn}</button>
                                      </div>
                                    )}
                                  </div>
                                )}
                                {a.post_meeting_applied && (
                                  <div style={{ fontSize: 11, color: '#16a34a', fontWeight: 700, marginTop: 6 }}>✓ {L.applied}</div>
                                )}
                              </div>
                            )}
                          </div>
                          {canEdit && selected.status !== 'held' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
                              <div style={{ display: 'flex', gap: 2 }}>
                                <button onClick={() => move(idx, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 12, opacity: idx === 0 ? 0.3 : 1 }}>▲</button>
                                <button onClick={() => move(idx, 1)} disabled={idx === arr.length - 1} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 12, opacity: idx === arr.length - 1 ? 0.3 : 1 }}>▼</button>
                              </div>
                              <button onClick={() => startEdit(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: 12 }}>✎</button>
                              <button onClick={() => removeItem(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 12 }}>✕</button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {canEdit && selected.status !== 'held' && (() => {
              const inAgenda = new Set((selected.agenda_items || []).filter(a => a.source_type).map(a => `${a.source_type}:${a.source_id}`));
              const other = openItems.filter(o => !inAgenda.has(`${o.source_type}:${o.source_id}`));
              if (other.length === 0) return null;
              return (
                <div style={{ ...card, marginTop: 14, padding: '12px 14px' }}>
                  <div onClick={() => setShowOther(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: '#374151' }}>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{showOther ? '▼' : '▶'}</span>
                    {L.otherOpen} ({other.length})
                  </div>
                  {showOther && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 10 }}>
                      {other.map(o => {
                        const bd = SOURCE_BADGE[o.source_type];
                        return (
                          <div key={`${o.source_type}:${o.source_id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', borderRadius: 6, background: '#fafafa' }}>
                            <span style={{ fontSize: 9, padding: '2px 6px', borderRadius: 999, background: bd.color + '18', color: bd.color, fontWeight: 700, flexShrink: 0 }}>{bd.icon} {bd.label}</span>
                            <span style={{ flex: 1, fontSize: 12.5, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.title}{o.owner ? ` — ${o.owner}` : ''}</span>
                            <button onClick={() => quickAddLinked(o)} title={L.addTopic}
                              style={{ background: '#eef2ff', color: '#4338ca', border: 'none', borderRadius: 6, width: 24, height: 24, cursor: 'pointer', fontSize: 14, fontWeight: 700, flexShrink: 0, lineHeight: 1 }}>+</button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}

            {selected.status === 'held' && !!selected.match_new_actions?.length && (
              <div style={{ ...card, marginTop: 14, padding: '14px 16px' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#111', marginBottom: 8 }}>{L.newActionsTitle}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {selected.match_new_actions.map((na, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: '#fafafa', borderRadius: 8, padding: '9px 12px' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#111' }}>{na.title}</div>
                        {na.assignee && <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>👤 {na.assignee}</div>}
                        {na.note && <div style={{ fontSize: 12, color: '#374151', marginTop: 2 }}>{na.note}</div>}
                      </div>
                      {canEdit && (
                        <button onClick={() => createActionTask(na)}
                          style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>{L.createTaskBtn}</button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selected.status === 'held' && !!selected.match_general_notes && (
              <div style={{ ...card, marginTop: 14, padding: '14px 16px', background: '#fffdf5', border: '1px solid #fde68a' }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>{L.generalNotesTitle}</div>
                <div style={{ fontSize: 13, color: '#374151', whiteSpace: 'pre-wrap' }}>{selected.match_general_notes}</div>
              </div>
            )}
          </>
        )}
      </div>

      {showComplete && selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 420 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px', color: '#111' }}>{L.completeTitle}</h3>
            <p style={{ fontSize: 13, color: '#374151', margin: '0 0 18px', lineHeight: 1.5 }}>{L.completeHint}</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowComplete(false)}
                style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>{C.cancel}</button>
              <button onClick={handleComplete} disabled={completing}
                style={{ background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: completing ? 0.6 : 1 }}>
                {completing ? C.saving : L.completeConfirm}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 480, maxHeight: '85vh', overflowY: 'auto' }}>
            <h2 style={{ fontSize: 18, fontWeight: 600, margin: '0 0 16px', color: '#111' }}>{L.newMeeting}</h2>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{L.fieldTitle} *</label>
              <input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{L.fieldType}</label>
              <input value={form.meeting_type} onChange={e => setForm({ ...form, meeting_type: e.target.value })}
                placeholder={L.fieldTypePh}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{L.fieldDate}</label>
              <input type="date" value={form.meeting_date} onChange={e => setForm({ ...form, meeting_date: e.target.value })}
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #e5e7eb', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{L.fieldParticipants}</label>
              <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8, padding: 8 }}>
                {stakeholders.length === 0 && <div style={{ fontSize: 12, color: '#9ca3af', padding: 4 }}>{L.noStakeholders}</div>}
                {stakeholders.map(s => (
                  <label key={s.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px', fontSize: 13, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.participant_stakeholder_ids.includes(s.id)} onChange={() => toggleParticipant(s.id)} />
                    <span>{s.name}{s.role ? ` — ${s.role}` : ''}{s.organization ? ` (${s.organization})` : ''}</span>
                  </label>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setShowCreate(false)}
                style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, cursor: 'pointer' }}>{C.cancel}</button>
              <button onClick={handleCreate} disabled={saving || !form.title.trim()}
                style={{ background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (saving || !form.title.trim()) ? 0.6 : 1 }}>
                {saving ? C.saving : L.createBtn}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 24, width: 380 }}>
            <p style={{ fontSize: 14, color: '#374151', margin: '0 0 16px' }}>{L.confirmDelete} "{deleteTarget.title}"?</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setDeleteTarget(null)}
                style={{ background: '#f3f4f6', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer' }}>{C.cancel}</button>
              <button onClick={confirmDelete}
                style={{ background: '#ef4444', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>{C.delete}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Meetings;
